import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

/**
 * Lookup values — TanStack Query hook (sdd/finanzas-modulo PR#3 T3.4).
 *
 * Read-only consumer of the new /lookup-values endpoint family (T2.10
 * from PR#2). The BE exposes the GLOBAL `lookup_values` table (no
 * RLS — shared reference dimension, not tenant-scoped). The wire
 * param is `?category=` to match the DB column name (NOT `?typeCode=`
 * — see obs-606 design decision).
 *
 * `staleTime: 5 min` because lookup vocabularies are near-static;
 * anything more frequent is a misconfiguration on the BE side.
 */

export interface LookupValueDto {
  id: string;
  category: string;
  code: string;
  label: string;
  /** snake_case from the BE (`sort_order`); the hook does NOT rewrite. */
  sort_order: number;
  is_active: boolean;
}

export interface LookupValuePage {
  items: LookupValueDto[];
}

export const lookupValuesKeys = {
  all: ['lookup-values'] as const,
  list: (category: string, isActive?: boolean) =>
    [...lookupValuesKeys.all, category, isActive ?? null] as const,
};

/**
 * `GET /lookup-values?category=<string>[&isActive=true|false]`.
 *
 * The `enabled` guard short-circuits the request when the caller
 * hasn't picked a category yet (typical during initial form
 * render — `category` starts as `null`).
 */
export function useLookupValues(category: string | null | undefined, isActive?: boolean) {
  const enabled = Boolean(category);
  return useQuery({
    queryKey: lookupValuesKeys.list(category ?? '', isActive),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('category', category as string);
      if (isActive !== undefined) sp.set('isActive', String(isActive));
      return apiFetch<LookupValuePage>(`/lookup-values?${sp.toString()}`);
    },
    enabled,
    staleTime: 300_000,
  });
}
